import { ConfirmDestructiveDialog } from "@/components/confirm-destructive-dialog";
import { SiteHeader } from "@/components/site-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { formatDateTime } from "@/lib/formatters";
import { queryKeys } from "@/lib/queryKeys";
import {
  type Announcement,
  type AnnouncementPayload,
  type AnnouncementTranslation,
  announcementsProvider,
  parseAnnouncementValidationError,
} from "@/providers/announcements.provider";
import { featureFlagsProvider } from "@/providers/feature-flags.provider";
import {
  IconEdit,
  IconExternalLink,
  IconPlus,
  IconSpeakerphone,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

// ── Types ─────────────────────────────────────────────────────────────────────

type SheetMode =
  | { type: "closed" }
  | { type: "create" }
  | { type: "edit"; announcement: Announcement };

type TimezoneMode = "all" | "none" | "specific";

const NO_FLAG = "__none__";

// ── Helpers ───────────────────────────────────────────────────────────────────

const toLocalDatetime = (iso: string) => iso.slice(0, 16);

function timezoneSummary(timezones: string[] | null): string {
  if (timezones === null) return "All timezones";
  if (timezones.length === 0) return "Hidden from everyone";
  if (timezones.length === 1) return timezones[0]!;
  return `${timezones.length} timezones`;
}

function windowSummary(announcement: Announcement): string {
  const start = formatDateTime(announcement.starts_at);
  const end = announcement.ends_at ? formatDateTime(announcement.ends_at) : "never";
  return `${start} → ${end}`;
}

// ── Row ───────────────────────────────────────────────────────────────────────

function AnnouncementRow({
  announcement,
  onEdit,
  onDelete,
  isDeletePending,
  onToggleEnabled,
  isTogglePending,
}: {
  announcement: Announcement;
  onEdit: (a: Announcement) => void;
  onDelete: (id: string) => void;
  isDeletePending: boolean;
  onToggleEnabled: (id: string, next: boolean) => void;
  isTogglePending: boolean;
}) {
  return (
    <TableRow>
      <TableCell className="text-center tabular-nums text-muted-foreground">
        {announcement.priority}
      </TableCell>
      <TableCell className="min-w-0 max-w-[220px]">
        <p className="truncate font-medium">{announcement.label || announcement.slug}</p>
        <p className="truncate font-mono text-xs text-muted-foreground">{announcement.slug}</p>
      </TableCell>
      <TableCell className="min-w-0 max-w-[220px] truncate text-xs text-muted-foreground">
        {windowSummary(announcement)}
      </TableCell>
      <TableCell className="min-w-0 max-w-[160px] truncate text-xs text-muted-foreground">
        {timezoneSummary(announcement.timezones)}
      </TableCell>
      <TableCell className="min-w-0 max-w-[160px] truncate">
        {announcement.feature_flag_key ? (
          <Badge variant="outline" className="font-mono">
            {announcement.feature_flag_key}
          </Badge>
        ) : (
          <span className="text-xs italic text-muted-foreground">ungated</span>
        )}
      </TableCell>
      <TableCell>
        <Switch
          checked={announcement.is_enabled}
          disabled={isTogglePending}
          onCheckedChange={(next) => onToggleEnabled(announcement.id, next)}
        />
      </TableCell>
      <TableCell>
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="size-9 sm:size-8"
            title="Edit announcement"
            onClick={() => onEdit(announcement)}
          >
            <IconEdit className="size-4" />
          </Button>
          <ConfirmDestructiveDialog
            confirmValue={announcement.slug}
            confirmLabel="slug"
            title={`Delete announcement "${announcement.slug}"?`}
            description="This permanently removes the slide. This action cannot be undone."
            isPending={isDeletePending}
            onConfirm={() => onDelete(announcement.id)}
            trigger={({ disabled }) => (
              <Button
                variant="ghost"
                size="icon"
                className="size-9 sm:size-8 text-destructive hover:text-destructive"
                title="Delete announcement"
                disabled={disabled}
              >
                <IconTrash className="size-4" />
              </Button>
            )}
          />
        </div>
      </TableCell>
    </TableRow>
  );
}

// ── Timezone chip editor ─────────────────────────────────────────────────────

function TimezoneEditor({
  mode,
  onModeChange,
  zones,
  onZonesChange,
}: {
  mode: TimezoneMode;
  onModeChange: (m: TimezoneMode) => void;
  zones: string[];
  onZonesChange: (zones: string[]) => void;
}) {
  const [draft, setDraft] = useState("");

  const allZones = useMemo(() => {
    try {
      const supportedValuesOf = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] })
        .supportedValuesOf;
      return supportedValuesOf ? supportedValuesOf("timeZone") : [];
    } catch {
      return [];
    }
  }, []);

  function addZone() {
    const value = draft.trim();
    if (!value || zones.includes(value)) return;
    onZonesChange([...zones, value]);
    setDraft("");
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-1.5">
        {(
          [
            { value: "all", label: "All timezones" },
            { value: "specific", label: "Specific list" },
            { value: "none", label: "Hidden from everyone" },
          ] as const
        ).map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onModeChange(opt.value)}
            className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
              mode === opt.value
                ? "border-primary bg-primary/10 text-primary"
                : "border-input text-muted-foreground hover:text-foreground"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {mode === "specific" && (
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <Input
              list="iana-timezones"
              placeholder="e.g. America/Sao_Paulo"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addZone();
                }
              }}
              className="font-mono"
            />
            <datalist id="iana-timezones">
              {allZones.map((z) => (
                <option key={z} value={z} />
              ))}
            </datalist>
            <Button type="button" variant="outline" onClick={addZone}>
              Add
            </Button>
          </div>
          {zones.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {zones.map((z) => (
                <Badge key={z} variant="secondary" className="gap-1 font-mono">
                  {z}
                  <button
                    type="button"
                    onClick={() => onZonesChange(zones.filter((x) => x !== z))}
                    className="ml-0.5"
                  >
                    <IconX className="size-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Create / Edit Sheet ───────────────────────────────────────────────────────

function AnnouncementFormSheet({
  mode,
  onClose,
}: {
  mode: Extract<SheetMode, { type: "create" | "edit" }>;
  onClose: () => void;
}) {
  const isEdit = mode.type === "edit";
  const existing = isEdit ? mode.announcement : null;
  const queryClient = useQueryClient();

  const { data: locales, isLoading: localesLoading } = useQuery({
    queryKey: queryKeys.locales(),
    queryFn: () => announcementsProvider.listLocales(),
  });

  const { data: flags } = useQuery({
    queryKey: queryKeys.featureFlags(),
    queryFn: () => featureFlagsProvider.list(),
  });

  const [slug, setSlug] = useState(existing?.slug ?? "");
  const [label, setLabel] = useState(existing?.label ?? "");
  const [iconName, setIconName] = useState(existing?.icon_name ?? "");
  const [accentColor, setAccentColor] = useState(existing?.accent_color ?? "#FF6B00");
  const [redirectPath, setRedirectPath] = useState(existing?.redirect_path ?? "");
  const [imageUrl, setImageUrl] = useState(existing?.image_url ?? "");
  const [priority, setPriority] = useState(existing?.priority ?? 0);
  const [startsAt, setStartsAt] = useState(
    existing ? toLocalDatetime(existing.starts_at) : "1900-01-01T00:00",
  );
  const [endsAt, setEndsAt] = useState(
    existing?.ends_at ? toLocalDatetime(existing.ends_at) : "",
  );
  const [timezoneMode, setTimezoneMode] = useState<TimezoneMode>(
    existing
      ? existing.timezones === null
        ? "all"
        : existing.timezones.length === 0
          ? "none"
          : "specific"
      : "all",
  );
  const [zones, setZones] = useState<string[]>(existing?.timezones ?? []);
  const [featureFlagKey, setFeatureFlagKey] = useState(existing?.feature_flag_key ?? NO_FLAG);
  const [translations, setTranslations] = useState<Record<string, AnnouncementTranslation>>(
    existing?.translations ?? {},
  );
  const [activeLocale, setActiveLocale] = useState<string | null>(null);

  // Locales load async — seed any missing tab once they arrive, without
  // clobbering values the admin is already editing.
  useEffect(() => {
    if (!locales?.length) return;
    setTranslations((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const locale of locales) {
        if (!next[locale.tag]) {
          next[locale.tag] = { title: "", body: "", cta_label: "" };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    setActiveLocale((prev) => prev ?? locales.find((l) => l.is_default)?.tag ?? locales[0]!.tag);
  }, [locales]);

  function updateTranslation(tag: string, field: keyof AnnouncementTranslation, value: string) {
    setTranslations((prev) => ({
      ...prev,
      [tag]: { ...prev[tag], [field]: value } as AnnouncementTranslation,
    }));
  }

  function handleMutationError(err: Error) {
    const validation = parseAnnouncementValidationError(err);
    if (validation) {
      toast.error(`${validation.field}: ${validation.error}`);
      const localeMatch = validation.field.match(/^translations\.(.+)$/);
      if (localeMatch) setActiveLocale(localeMatch[1]!);
      return;
    }
    toast.error(err.message);
  }

  const createMutation = useMutation({
    mutationFn: (payload: AnnouncementPayload) => announcementsProvider.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.announcements() });
      toast.success("Announcement created — it starts disabled until you publish it.");
      onClose();
    },
    onError: handleMutationError,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: AnnouncementPayload }) =>
      announcementsProvider.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.announcements() });
      toast.success("Announcement updated");
      onClose();
    },
    onError: handleMutationError,
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!startsAt) {
      toast.error("Start date is required");
      return;
    }

    const payload: AnnouncementPayload = {
      slug,
      translations,
      label: label || undefined,
      icon_name: iconName || undefined,
      accent_color: accentColor || undefined,
      redirect_path: redirectPath || undefined,
      image_url: imageUrl || null,
      priority,
      starts_at: new Date(startsAt).toISOString(),
      ends_at: endsAt ? new Date(endsAt).toISOString() : null,
      timezones: timezoneMode === "all" ? null : timezoneMode === "none" ? [] : zones,
      feature_flag_key: featureFlagKey === NO_FLAG ? null : featureFlagKey,
    };

    if (isEdit && existing) {
      updateMutation.mutate({ id: existing.id, payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex h-full flex-col">
      <SheetHeader>
        <SheetTitle>{isEdit ? "Edit announcement" : "New announcement"}</SheetTitle>
        <SheetDescription>
          {isEdit
            ? "Changes to a full replace of the editable fields — enable/disable stays separate."
            : "New announcements start disabled. Publish from the list once you're ready."}
        </SheetDescription>
      </SheetHeader>

      <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-4 py-2">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ann-slug">Slug</Label>
            <Input
              id="ann-slug"
              placeholder="welcome-promo"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              required
              className="font-mono"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ann-priority">Priority</Label>
            <Input
              id="ann-priority"
              type="number"
              value={priority}
              onChange={(e) => setPriority(Number(e.target.value))}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ann-label">Label</Label>
            <Input
              id="ann-label"
              placeholder="PROMO"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ann-icon">Icon name</Label>
            <Input
              id="ann-icon"
              placeholder="star"
              value={iconName}
              onChange={(e) => setIconName(e.target.value)}
              className="font-mono"
            />
            <a
              href="https://phosphoricons.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground underline hover:text-foreground"
            >
              Browse icon names on Phosphor Icons
              <IconExternalLink className="size-3" />
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ann-accent">Accent color</Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={/^#[0-9a-fA-F]{6}$/.test(accentColor) ? accentColor : "#FF6B00"}
                onChange={(e) => setAccentColor(e.target.value)}
                className="size-9 shrink-0 rounded-md border border-input"
              />
              <Input
                id="ann-accent"
                value={accentColor}
                onChange={(e) => setAccentColor(e.target.value)}
                className="font-mono"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ann-redirect">Redirect path</Label>
            <Input
              id="ann-redirect"
              placeholder="/promo"
              value={redirectPath}
              onChange={(e) => setRedirectPath(e.target.value)}
              className="font-mono"
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ann-image">Image URL</Label>
          <Input
            id="ann-image"
            placeholder="https://…"
            value={imageUrl ?? ""}
            onChange={(e) => setImageUrl(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ann-starts">Starts at</Label>
            <Input
              id="ann-starts"
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              required
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ann-ends">Ends at</Label>
            <Input
              id="ann-ends"
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">Leave blank for never-expires.</p>
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>Timezones</Label>
          <TimezoneEditor
            mode={timezoneMode}
            onModeChange={setTimezoneMode}
            zones={zones}
            onZonesChange={setZones}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ann-flag">Feature flag gate</Label>
          <Select value={featureFlagKey} onValueChange={setFeatureFlagKey}>
            <SelectTrigger id="ann-flag" className="w-full">
              <SelectValue placeholder="Ungated" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_FLAG}>Ungated — always eligible</SelectItem>
              {flags?.map((flag) => (
                <SelectItem key={flag.key} value={flag.key} className="font-mono">
                  {flag.key}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            The flag must already exist — a typo here hides the slide silently.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <Label>Translations</Label>
          {localesLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : !locales?.length ? (
            <p className="text-xs text-muted-foreground">No locales configured.</p>
          ) : (
            <Tabs value={activeLocale ?? locales[0]!.tag} onValueChange={setActiveLocale}>
              <TabsList className="w-full flex-wrap">
                {locales.map((locale) => (
                  <TabsTrigger key={locale.tag} value={locale.tag}>
                    {locale.native_name}
                  </TabsTrigger>
                ))}
              </TabsList>
              {locales.map((locale) => (
                <TabsContent key={locale.tag} value={locale.tag} className="flex flex-col gap-3">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor={`ann-title-${locale.tag}`}>Title</Label>
                    <Input
                      id={`ann-title-${locale.tag}`}
                      value={translations[locale.tag]?.title ?? ""}
                      onChange={(e) => updateTranslation(locale.tag, "title", e.target.value)}
                      required
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor={`ann-body-${locale.tag}`}>Body</Label>
                    <Textarea
                      id={`ann-body-${locale.tag}`}
                      value={translations[locale.tag]?.body ?? ""}
                      onChange={(e) => updateTranslation(locale.tag, "body", e.target.value)}
                      required
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor={`ann-cta-${locale.tag}`}>CTA label</Label>
                    <Input
                      id={`ann-cta-${locale.tag}`}
                      value={translations[locale.tag]?.cta_label ?? ""}
                      onChange={(e) => updateTranslation(locale.tag, "cta_label", e.target.value)}
                      required={!!redirectPath}
                    />
                    <p className="text-xs text-muted-foreground">
                      Text shown on the slide's action button, e.g. "Go". Required in every
                      locale when a redirect path is set.
                    </p>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          )}
        </div>
      </div>

      <SheetFooter>
        <Button type="submit" disabled={isPending} className="w-full">
          {isPending ? "Saving…" : isEdit ? "Save changes" : "Create announcement"}
        </Button>
      </SheetFooter>
    </form>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function AnnouncementsPage() {
  const [sheet, setSheet] = useState<SheetMode>({ type: "closed" });
  const queryClient = useQueryClient();

  const {
    data: announcements,
    isLoading,
    isError,
  } = useQuery({
    queryKey: queryKeys.announcements(),
    queryFn: () => announcementsProvider.list(),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isEnabled }: { id: string; isEnabled: boolean }) =>
      announcementsProvider.setEnabled(id, isEnabled),
    onSuccess: (_, { id, isEnabled }) => {
      queryClient.setQueryData<Announcement[]>(
        queryKeys.announcements(),
        (prev) => prev?.map((a) => (a.id === id ? { ...a, is_enabled: isEnabled } : a)) ?? [],
      );
      toast.success(isEnabled ? "Announcement published" : "Announcement disabled");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => announcementsProvider.delete(id),
    onSuccess: (_, id) => {
      queryClient.setQueryData<Announcement[]>(
        queryKeys.announcements(),
        (prev) => prev?.filter((a) => a.id !== id) ?? [],
      );
      toast.success("Announcement deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const isSheetOpen = sheet.type !== "closed";

  return (
    <div className="flex h-full flex-col">
      <SiteHeader title="Announcements" />

      <div className="flex flex-col gap-4 p-4 lg:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="max-w-xl text-sm text-muted-foreground">
            Authored slides shown in the app's announcement deck, alongside the hardcoded
            welcome/events slides. Sorted by priority, then start date.
          </p>
          <Button
            size="sm"
            className="shrink-0 gap-1.5"
            onClick={() => setSheet({ type: "create" })}
          >
            <IconPlus className="size-4" />
            New announcement
          </Button>
        </div>

        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-14 text-center">Priority</TableHead>
                <TableHead>Slide</TableHead>
                <TableHead>Window</TableHead>
                <TableHead>Timezones</TableHead>
                <TableHead>Flag gate</TableHead>
                <TableHead>Enabled</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 7 }).map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-12 text-center text-muted-foreground">
                    Failed to load announcements. Check your connection and try again.
                  </TableCell>
                </TableRow>
              ) : !announcements?.length ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-12 text-center text-muted-foreground">
                    <div className="flex flex-col items-center gap-2">
                      <IconSpeakerphone className="size-8 opacity-40" />
                      <p>No announcements yet.</p>
                      <button
                        className="underline hover:text-foreground"
                        onClick={() => setSheet({ type: "create" })}
                      >
                        Create one
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                announcements.map((announcement) => (
                  <AnnouncementRow
                    key={announcement.id}
                    announcement={announcement}
                    onEdit={(a) => setSheet({ type: "edit", announcement: a })}
                    onDelete={(id) => deleteMutation.mutate(id)}
                    isDeletePending={
                      deleteMutation.isPending && deleteMutation.variables === announcement.id
                    }
                    onToggleEnabled={(id, next) => toggleMutation.mutate({ id, isEnabled: next })}
                    isTogglePending={
                      toggleMutation.isPending &&
                      toggleMutation.variables?.id === announcement.id
                    }
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Sheet
        open={isSheetOpen}
        onOpenChange={(open) => {
          if (!open) setSheet({ type: "closed" });
        }}
      >
        <SheetContent
          side="right"
          className="flex w-full flex-col max-w-[calc(100%-2rem)] sm:max-w-lg"
        >
          {(sheet.type === "create" || sheet.type === "edit") && (
            <AnnouncementFormSheet mode={sheet} onClose={() => setSheet({ type: "closed" })} />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
